import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { getVariant } from "@/lib/ab";
import HeroProofStrip from "@/components/landing/HeroProofStrip";

const VERDICTS = ["PASS", "REJECT", "REVISIT", "INVEST", "BUY"];

/** Copy block per variant — keeps the visual rhythm (yellow accents + italic
 *  sub) consistent so we're testing the message, not the layout. Attribution
 *  runs through the existing `ab_variants` payload on Stripe checkout, so
 *  paid conversions land on `payment_transactions.ab_variants.hero_headline`.
 */
const COPY = {
  A: {
    line1: <>Step into <span className="text-[#E5FE40]">the tank.</span></>,
    line2: (
      <>
        Survive <span className="text-[#E5FE40]">five sharks</span> in{" "}
        <span className="text-[#E5FE40]">sixty seconds</span>
      </>
    ),
    italic: <>&mdash; or find out why you won&apos;t.</>,
    body: (
      <>
        The AI panel every VC wishes existed &mdash; a hostile investor,
        a skeptical customer, a ruthless competitor, a domain expert, and
        the devil&apos;s advocate. Zero mercy. Brutal scores. The one flaw
        that would sink your idea &mdash; before 18 months and $200k are gone.
      </>
    ),
  },
  // E — outcome-led value prop: names the honest-feedback gap, then the verdict.
  E: {
    line1: (
      <>
        Everyone says your idea is <span className="text-[#E5FE40]">&ldquo;interesting.&rdquo;</span>
      </>
    ),
    line2: <>Find out if it&apos;s actually <span className="text-[#E5FE40]">good.</span></>,
    italic: null,
    body: (
      <>
        Five hostile AI experts read your idea and tell you what your advisors
        won&apos;t &mdash; a 0&ndash;100 Pivot Score and the flaw that kills it.
        Sixty seconds. $29.
      </>
    ),
  },
  // Challenger pool — promoted automatically by the backend auto-retire loop
  // once one live variant beats the other by >=20% CR over >=200 starts each.
  F: {
    line1: <>Don&apos;t spend six months</>,
    line2: <>building the <span className="text-[#E5FE40]">wrong thing.</span></>,
    italic: null,
    body: (
      <>
        A skeptical VC, a customer with a workaround, a competitor, a domain
        expert and a devil&apos;s advocate score your idea 0&ndash;100 &mdash;
        with the reasons &mdash; before you write a line of code. Sixty seconds. $29.
      </>
    ),
  },
  G: {
    line1: <>You asked ChatGPT to be <span className="text-[#E5FE40]">brutally honest.</span></>,
    line2: <>It said &ldquo;strong potential.&rdquo;</>,
    italic: <>PivotProof won&apos;t.</>,
    body: (
      <>
        Five adversarial AI experts paid to find the flaw. One 0&ndash;100 Pivot
        Score. The real reasons your idea dies &mdash; in sixty seconds. $29.
      </>
    ),
  },
};

export default function Hero() {
  const { user } = useAuth();
  const variant = getVariant("hero_headline") || "A";
  const copy = COPY[variant] || COPY.A;

  // Fire a GA4 view event once per mount so we can layer this on top of
  // the paid-conversion attribution that already ships via ab_variants.
  useEffect(() => {
    try {
      window.gtag?.("event", "hero_variant_view", {
        experiment_id: "hero_headline",
        variant,
      });
    } catch {
      /* GA not loaded in preview — silent */
    }
  }, [variant]);

  const trackCta = () => {
    try {
      window.gtag?.("event", "hero_cta_click", {
        experiment_id: "hero_headline",
        variant,
      });
    } catch {
      /* silent */
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const startLogin = () => {
    trackCta();
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <section className="relative border-b border-white/10 overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <img
          src="/hero/tank-1024.webp"
          srcSet="/hero/tank-640.webp 640w, /hero/tank-1024.webp 1024w, /hero/tank-1600.webp 1600w"
          sizes="100vw"
          alt="Founder stress-testing a startup idea with a hostile AI expert panel"
          width="1600"
          height="1060"
          fetchPriority="high"
          decoding="async"
          className="w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/60 via-[#050505]/85 to-[#050505]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-10 md:pt-16 pb-14 md:pb-20 relative">
        <div className="tag mb-5 fade-up" data-testid="hero-tag">
          <span className="w-1.5 h-1.5 bg-[#E5FE40]" /> FOR SOLO FOUNDERS AND INDIE HACKERS · STARTUP IDEA VALIDATION
        </div>
        <h1
          className="font-display font-black text-[2.6rem] sm:text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.92] max-w-5xl fade-up"
          data-testid="hero-title"
          data-variant={variant}
        >
          {copy.line1}
          <br />
          {copy.line2}
          {copy.italic && (
            <span className="italic font-normal text-3xl md:text-4xl lg:text-5xl text-white/85 block mt-3 leading-[1.05]">
              {copy.italic}
            </span>
          )}
        </h1>

        <p className="mt-5 max-w-2xl text-sm md:text-lg text-white/70 fade-up" data-testid="hero-sub">
          {copy.body}
        </p>

        <div className="mt-7 flex flex-wrap items-center gap-4 fade-up">
          {user ? (
            <Link
              to="/new"
              data-testid="hero-cta-new"
              onClick={trackCta}
              className="btn-primary"
            >
              New Pivot Session <ArrowRight size={16} />
            </Link>
          ) : (
            <button data-testid="hero-cta-start" onClick={startLogin} className="btn-primary">
              TEST MY IDEA FOR $29 <ArrowRight size={16} />
            </button>
          )}
          <span className="font-mono text-xs text-white/40 uppercase tracking-[0.2em] ml-2">
            From $29 · credits never expire
          </span>
        </div>
        <HeroProofStrip />

        <div className="mt-14 border-t border-b border-white/10 overflow-hidden">
          <div className="marquee-track flex gap-12 py-4 whitespace-nowrap">
            {[...VERDICTS, ...VERDICTS, ...VERDICTS, ...VERDICTS, ...VERDICTS, ...VERDICTS].map((v, i) => (
              <span key={`${v}-${i}`} className="font-display font-black text-2xl md:text-3xl text-white/30 tracking-tight">
                {v} <span className="text-[#E5FE40]">/</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
